import React, { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes as ReactRouterRoutes, matchPath, useLocation } from "react-router-dom";

import isAuthenticated, { FEATURE_ENABLED, PLAN_CHANGED_EVENT, getFeatureState } from '../services/auth';

import Sidebar from '../components/Sidebar';
import Icon from '../components/Icon';
import Login from '../pages/Login';
import PasswordRecovery from '../pages/PasswordRecovery';
import privateRoutes, { getInitialPath } from './privateRoutes';
import PasswordReset from '../pages/PasswordReset';
import PasswordTokenValidate from '../pages/PasswordTokenValidate';
import NotFound from '../pages/NotFound';
import PanelTemplate from '../templates/Panel';
import { GestaoTeaserPage } from '../components/GestaoTeaser';

// Rotas sem o shell do painel: padrões (matchPath), não prefixos, para que
// /proposal sem id ou uma futura /proposal-templates não colidam por engano.
const NO_SIDEBAR_ROUTES = [
    '/login',
    '/password-recovery',
    '/password-reset',
    '/password-reset/:recovery_token',
    '/proposal/:id',
];

// Monta o Sidebar uma vez, fora do <Routes>, para não remontar a cada navegação.
const PanelShell = ({ children }) => {
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [planVersion, setPlanVersion] = useState(0);

    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    // Plano reconferido diferente do que veio no login: a tela aberta remonta
    // para refletir o módulo liberado (ou retirado) sem exigir novo login.
    useEffect(() => {
        const remount = () => setPlanVersion(version => version + 1);

        window.addEventListener(PLAN_CHANGED_EVENT, remount);
        return () => window.removeEventListener(PLAN_CHANGED_EVENT, remount);
    }, []);

    useEffect(() => {
        document.body.classList.toggle('sidebar-mobile-open', sidebarOpen);
        return () => document.body.classList.remove('sidebar-mobile-open');
    }, [sidebarOpen]);

    const hideSidebar = !isAuthenticated()
        || NO_SIDEBAR_ROUTES.some((pattern) => matchPath(pattern, location.pathname));

    if (hideSidebar) {
        return children;
    }

    return (
        <main id="panel" className={sidebarOpen ? 'sidebar-open' : ''}>
            <button
                type="button"
                className="mobile-menu-button"
                aria-label="Abrir menu"
                aria-expanded={sidebarOpen}
                onClick={() => setSidebarOpen(true)}
            >
                <Icon name="menu" size={22} />
            </button>
            <button
                type="button"
                className="sidebar-overlay"
                aria-label="Fechar menu"
                onClick={() => setSidebarOpen(false)}
            />
            <Sidebar mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
            <React.Fragment key={planVersion}>{children}</React.Fragment>
        </main>
    );
};

const Routes = () => {
    const RequireAuth = ({children, route}) => {
        let location = useLocation();
        localStorage.setItem('lastPath', location.pathname);
        
        if(!isAuthenticated()){
            return <Navigate to="/login" state={{ from: location }} />;
        }

        if(!route.show){
            let initialPath = getInitialPath();
            return <Navigate to={initialPath} state={{ from: location }} />;
        }

        if(route.module && getFeatureState(route.module) !== FEATURE_ENABLED){
            return (
                <PanelTemplate id="module-unavailable">
                    <GestaoTeaserPage state={getFeatureState(route.module)} />
                </PanelTemplate>
            );
        }

        return children;
    };

    return (
        <BrowserRouter>
            <PanelShell>
                <ReactRouterRoutes>
                    <Route exact path="/" element={isAuthenticated() ? <Navigate to={localStorage.getItem('lastPath') ?? '/dashboard'} /> : <Navigate to="/login" /> } />
                    <Route path="/login" element={<Login />} />
                    <Route path="/password-recovery" element={<PasswordRecovery />} />
                    <Route path="/password-reset" element={<PasswordReset />} />
                    <Route path="/password-reset/:recovery_token" element={<PasswordTokenValidate />} />

                    {privateRoutes.map((privateRoute, index) => (
                        <Route key={index} path={privateRoute.path} element={<RequireAuth route={privateRoute}>{privateRoute.element}</RequireAuth>} />
                    ))}
                    <Route path="*" element={isAuthenticated() ? <NotFound /> : <Navigate to="/login" />} />
                </ReactRouterRoutes>
            </PanelShell>
        </BrowserRouter>
    );
}

export default Routes;